import ListSearchBar from "../components/ListSearchBar";
/* eslint-disable eqeqeq */
import { useEffect, useState } from "react";
import { Dropdown, Table } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { useTranslate } from "../hooks/LanguageProvider";
import { PropagateLoader } from "react-spinners";
import {
  DownloadModal,
  generateExcelFile,
  generatePDFFile,
  handleDownloadConfirm,
  toTimestamp,
} from "../utilities/functions";
import { toast } from "react-toastify";
import { usePagePreferences } from "../hooks/usePagePreferences";
import UserHistoryDrawer, {
  OperationBadge,
  UserHistoryItem,
} from "../components/UserHistory/UserHistoryDrawer";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const HistoryUser: React.FC = () => {
  const { translate } = useTranslate();
  const user_id = localStorage.getItem("userid");

  const [list_history, setHistories] = useState<UserHistoryItem[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [total, setTotal] = useState(0);
  const {
    preferences: listPreferences,
    setPreference: setListPreference,
    setPreferences: setListPreferences,
    loaded: listPreferencesLoaded,
  } = usePagePreferences("history-user", {
    limitValue: 10,
    currentPage: 1,
    search: "",
    type: 0,
    typeSearch: "Utilisateur",
    column: "date",
    sort: "DESC",
  });
  const [limitValue, setLimitValue] = useState(Number(listPreferences.limitValue) || 10);
  const [currentPage, setCurrentPage] = useState(Number(listPreferences.currentPage) || 1);
  const [loading, setLoading] = useState(true);

  // Recherche et tri
  const [search, setSearch] = useState(String(listPreferences.search || ""));
  const [type, setType] = useState(Number(listPreferences.type) || 0);
  const [selectedSearch, setSelectedSearch] = useState(translate("Utilisateur"));
  const [typeSearch, setTypeSearch] = useState(String(listPreferences.typeSearch || "Utilisateur"));
  const [column, setSortColumn] = useState(String(listPreferences.column || "date"));
  const [sort, setSort] = useState(String(listPreferences.sort || "DESC"));

  // Sélection
  const [selectedHistories, setSelectedHistories] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  // Export / Suppression
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<UserHistoryItem | null>(null);
  const [showHistoryDrawer, setShowHistoryDrawer] = useState(false);

  const searchColumn: { [key: string]: number } = {
    utilisateur: 0,
    operation: 1,
    module: 2,
    type: 3,
    date: 4,
  };

  const Options = ["Utilisateur", "Opération", "Module", "Type", "Date"];

  const handleTypeSearch = (event: any) => {
    const selectedValue = event.target.textContent;
    let nextType = type;
    switch (selectedValue) {
      case translate("Utilisateur"):
        nextType = 0;
        break;
      case translate("Opération"):
        nextType = 1;
        break;
      case translate("Module"):
        nextType = 2;
        break;
      case translate("Type"):
        nextType = 3;
        break;
      case translate("Date"):
        nextType = 4;
        break;
      default:
        break;
    }
    setType(nextType);
    setSelectedSearch(translate(selectedValue));
    setTypeSearch(selectedValue);
    setCurrentPage(1);
    setListPreferences({ type: nextType, typeSearch: selectedValue, currentPage: 1 });
  };

  const handleSortingColumn = (currentColumn: string) => {
    const nextSort = sort === "DESC" ? "ASC" : "DESC";
    setSort(nextSort);
    setSortColumn(currentColumn);
    setCurrentPage(1);
    setListPreferences({ column: currentColumn, sort: nextSort, currentPage: 1 });
  };

  const handleSelectChange = async (event: any) => {
    const newValue = parseInt(event.target.value);
    setCurrentPage(1);
    setLimitValue(newValue);
    setListPreferences({ limitValue: newValue, currentPage: 1 });
  };

  const handleAdvancedSearch = async (event: any) => {
    const nextSearch = event.target.value;
    setSearch(nextSearch);
    setCurrentPage(1);
    setListPreferences({ search: nextSearch, currentPage: 1 });
  };

  const handleResetSearch = async () => {
    setSearch("");
    setCurrentPage(1);
    setListPreferences({ search: "", currentPage: 1 });
  };

  const handlePageClick = async (data: any) => {
    let newPage = data.selected + 1;
    setCurrentPage(newPage);
    setListPreference("currentPage", newPage);
    window.scrollTo(0, 0);
  };

  const getHistories = async (
    limitValue: number,
    currentPage: number,
    search: string,
    type: number,
    column: string,
    sort: string
  ) => {
    const bodyData = JSON.stringify({
      limitValue,
      currentPage,
      search,
      type,
      id_user: user_id,
      column: searchColumn[column] ?? 0,
      sort,
    });

    try {
      setLoading(true);

      const total_pages = await fetch(
        `${backendUrl}/api/user-history/search/totalpage`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: bodyData,
          mode: "cors",
        }
      );
      const totalPages = await total_pages.json();
      const totalCount = totalPages[0]?.total ?? 0;
      setTotal(totalCount);

      const res = await fetch(`${backendUrl}/api/user-history/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: bodyData,
        mode: "cors",
      });

      if (!res.ok) {
        console.error("Erreur récupération historique utilisateur");
        setLoading(false);
        return;
      }

      const data = await res.json();
      setPageCount(Math.ceil(totalCount / limitValue));
      setHistories(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!listPreferencesLoaded) return;

    const nextLimit = Number(listPreferences.limitValue) || 10;
    const nextPage = Number(listPreferences.currentPage) || 1;
    const nextSearch = String(listPreferences.search || "");
    const nextType = Number(listPreferences.type) || 0;
    const nextTypeSearch = String(listPreferences.typeSearch || "Utilisateur");
    const nextColumn = String(listPreferences.column || "date");
    const nextSort = String(listPreferences.sort || "DESC");

    setLimitValue(nextLimit);
    setCurrentPage(nextPage);
    setSearch(nextSearch);
    setType(nextType);
    setTypeSearch(nextTypeSearch);
    setSelectedSearch(translate(nextTypeSearch));
    setSortColumn(nextColumn);
    setSort(nextSort);
    getHistories(nextLimit, nextPage, nextSearch, nextType, nextColumn, nextSort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_id, listPreferencesLoaded, listPreferences]);

  useEffect(() => {
    if (!listPreferencesLoaded) return;
    getHistories(limitValue, currentPage, search, type, column, sort);
  }, [limitValue, currentPage, search, type, column, sort, user_id, listPreferencesLoaded]);

  // Gestion sélection
  const handleSelectAllHistories = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      const allIDs = list_history.map((h) => h.idlog.toString());
      setSelectedHistories(allIDs);
    } else {
      setSelectedHistories([]);
    }
  };

  const handleHistorySelect = (HistoryID: string) => {
    let updated: string[] = [];
    if (selectedHistories.includes(HistoryID)) {
      updated = selectedHistories.filter((id) => id !== HistoryID);
    } else {
      updated = [...selectedHistories, HistoryID];
    }
    setSelectedHistories(updated);
    if (updated.length === 0) setSelectAll(false);
  };

  // Export Excel
  const historyHeaders = [
    translate("Utilisateur"),
    translate("Opération"),
    translate("Module"),
    translate("Type"),
    translate("Date"),
    translate("Informations"),
  ];

  const downloadExcel = () => {
    const selectedData = list_history
      .filter((h) => selectedHistories.includes(h.idlog.toString()))
      .map((h) => [
        `${h.nom_user} ${h.prenom_user}`,
        h.operation_user,
        h.table_operation,
        h.type_operation,
        toTimestamp(h.date_operation),
        h.info_operation,
      ]);
    generateExcelFile(
      translate("List") + " " + translate("User history"),
      historyHeaders,
      selectedData
    );
  };

  const downloadPDF = () => {
    const selectedData = list_history
      .filter((h) => selectedHistories.includes(h.idlog.toString()))
      .map((h) => [
        `${h.nom_user} ${h.prenom_user}`,
        h.operation_user,
        h.table_operation,
        h.type_operation,
        toTimestamp(h.date_operation),
        h.info_operation,
      ]);
    generatePDFFile(
      translate("List") + " " + translate("User history"),
      historyHeaders,
      selectedData
    );
  };

  const onDownloadConfirm = (format: string) => {
    if (selectedHistories.length > 0) {
      handleDownloadConfirm(format, downloadExcel, downloadPDF);
    } else {
      toast.warn("Please select at least one record", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  const handleOpenHistoryDrawer = (history: UserHistoryItem) => {
    setSelectedHistory(history);
    setShowHistoryDrawer(true);
  };

  const [selectedColumns, setSelectedColumns] = useState({
    utilisateur: true,
    matricule: true,
    operation: true,
    module: true,
    type: true,
    date: true,
    info: true,
    actions: true,
  });

  const [vehicleMatricules, setVehicleMatricules] = useState<Record<string, string>>({});

  const totalTableColumns = 1 + Object.values(selectedColumns).filter(Boolean).length;

  const handleColumnChange = (column: string) => {
    setSelectedColumns((prevState: any) => ({
      ...prevState,
      [column]: !prevState[column],
    }));
  };

  const extractVehicleId = (info?: string) => {
    if (!info) return null;
    const match = info.match(/vehicule[:\s]+(\d+)/i) || info.match(/vehicle[:\s]+(\d+)/i);
    return match?.[1] ?? null;
  };

  const fetchVehicleMatricule = async (vehicleId: string) => {
    try {
      const response = await fetch(`${backendUrl}/api/vehicle/find/${vehicleId}`);
      if (!response.ok) return null;
      const data = await response.json();
      return data?.immatriculation_vehicule ?? null;
    } catch (error) {
      console.error("Error fetching vehicle matricule:", error);
      return null;
    }
  };

  useEffect(() => {
    const vehicleIds = Array.from(
      new Set(
        list_history
          .map((history) => extractVehicleId(history.info_operation))
          .filter((id): id is string => Boolean(id))
      )
    );

    const idsToLoad = vehicleIds.filter((id) => !vehicleMatricules[id]);
    if (idsToLoad.length === 0) return;

    const loadMatricules = async () => {
      const entries = await Promise.all(
        idsToLoad.map(async (vehicleId) => {
          const matricule = await fetchVehicleMatricule(vehicleId);
          return [vehicleId, matricule] as const;
        })
      );
      setVehicleMatricules((prev) => ({
        ...prev,
        ...Object.fromEntries(entries.filter(([, matricule]) => matricule)),
      }));
    };

    loadMatricules();
  }, [list_history, vehicleMatricules]);

  return (
    <>
      <div className="row">
        <h4 className="col-sm-6 col-md-6">
          <i className="las la-history"></i>{" "}
          {translate("Historique des utilisateurs")} ({total})
        </h4>
        <div className="col-sm-6 col-md-6 d-flex justify-content-end">
          <button
            className="btn btn-outline-secondary"
            onClick={() => setShowDownloadModal(true)}
          >
            <i className="las la-download"></i> {translate("Export")}
          </button>
        </div>
      </div>

      {/* Barre recherche */}
      <div className="row">
        <div className="col-md-4 list-search-column">
          <ListSearchBar criteria={<Dropdown>
              <Dropdown.Toggle
                variant="link"
                id="dropdown-custom"
                className="custom-toggle"
              >{translate(typeSearch)}</Dropdown.Toggle>
              <Dropdown.Menu onClick={handleTypeSearch}>
                {Options.map((option) => (
                  <Dropdown.Item
                    key={option}
                    className={
                      selectedSearch === translate(option) ? "active" : ""
                    }
                    onClick={() => setSelectedSearch(translate(option))}
                  >
                    {translate(option)}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>} onReset={handleResetSearch} resetLabel={translate("Reset search")}>
<input
              type="text"
              placeholder={` ${translate("By")} ${typeSearch}`}
              value={search}
              onChange={handleAdvancedSearch}
              className="form-control"
            />
</ListSearchBar>
        </div>



        <div className="col-md-8 d-flex justify-content-end align-items-center">
          <div className="dataTables_length" id="DataTables_Table_0_length">
            <label style={{ marginBottom: 0 }}>
              {translate("Show")}
              <select
                className="custom-select custom-select-sm form-control form-control-sm ml-2"
                style={{ width: "66px" }}
                onChange={handleSelectChange}
                value={limitValue}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
                 <option value="1000">1000</option>
              </select>
            </label>
          </div>
          <Dropdown>
            <Dropdown.Toggle
              variant=""
              id="dropdown-basic"
              title={translate("Display columns")}
            >
              <i className="las la-eye"></i>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.utilisateur}
                  onChange={() => handleColumnChange("utilisateur")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("Utilisateur")}
                </span>
              </Dropdown.Item>

              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.operation}
                  onChange={() => handleColumnChange("operation")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("Opération")}
                </span>
              </Dropdown.Item>

              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.module}
                  onChange={() => handleColumnChange("module")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("Module")}
                </span>
              </Dropdown.Item>

              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.type}
                  onChange={() => handleColumnChange("type")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("Type")}
                </span>
              </Dropdown.Item>

              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.matricule}
                  onChange={() => handleColumnChange("matricule")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("Matricule")}
                </span>
              </Dropdown.Item>

              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.date}
                  onChange={() => handleColumnChange("date")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("Date")}
                </span>
              </Dropdown.Item>

              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.info}
                  onChange={() => handleColumnChange("info")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("Informations")}
                </span>
              </Dropdown.Item>

              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.actions}
                  onChange={() => handleColumnChange("actions")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("Actions")}
                </span>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      {/* Tableau */}
      <div className="table-responsive">
        <Table className="dataTable">
          <thead className="bg-white text-uppercase">
            <tr className="ligth ligth-data">
              <th>
                <div className="checkbox d-inline-block">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectAll}
                    onChange={(e) => handleSelectAllHistories(e.target.checked)}
                  />
                  <label className="form-check-label"></label>
                </div>
              </th>

              {selectedColumns.utilisateur && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColumn("utilisateur")}
                >
                  {translate("Utilisateur")}
                </th>
              )}

              {selectedColumns.operation && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColumn("operation")}
                >
                  {translate("Opération")}
                </th>
              )}

              {selectedColumns.matricule && <th>{translate("Matricule")}</th>}

              {selectedColumns.module && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColumn("module")}
                >
                  {translate("Module")}
                </th>
              )}

              {selectedColumns.type && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColumn("type")}
                >
                  {translate("Type")}
                </th>
              )}

              {selectedColumns.date && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColumn("date")}
                >
                  {translate("Date")}
                </th>
              )}

              {selectedColumns.info && <th>{translate("Informations")}</th>}

              {selectedColumns.actions && <th>{translate("Actions")}</th>}
            </tr>
          </thead>

          <tbody key="#" className="ligth-body">
            {loading ? (
              <tr>
                <td colSpan={totalTableColumns}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <PropagateLoader
                      color={"#123abc"}
                      loading={loading}
                      size={20}
                    />
                  </div>
                </td>
              </tr>
            ) : list_history.length > 0 ? (
              list_history.map((item) => (
                <tr key={item.idlog}>
                  <td>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selectedHistories.includes(
                        item.idlog.toString()
                      )}
                      onChange={() =>
                        handleHistorySelect(item.idlog.toString())
                      }
                    />
                  </td>
                  {selectedColumns.utilisateur && (
                    <td>{`${item.nom_user} ${item.prenom_user}`}</td>
                  )}

                  {selectedColumns.operation && (
                    <td>
                      <OperationBadge operation={item.operation_user} />
                    </td>
                  )}

                  {selectedColumns.matricule && (
                    <td>
                      {vehicleMatricules[extractVehicleId(item.info_operation) ?? ""] || "-"}
                    </td>
                  )}

                  {selectedColumns.module && (
                    <td>{item.table_operation}</td>
                  )}

                  {selectedColumns.type && (
                    <td>{item.type_operation}</td>
                  )}

                  {selectedColumns.date && (
                    <td>{toTimestamp(item.date_operation)}</td>
                  )}

                  {selectedColumns.info && (
                    <td>{item.info_operation}</td>
                  )}

                  {selectedColumns.actions && (
                    <td>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        title={translate("View details")}
                        onClick={() => handleOpenHistoryDrawer(item)}
                      >
                        <i className="las la-eye"></i>
                      </button>
                    </td>
                  )}

                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={totalTableColumns} className="text-center">
                  {translate("Aucun historique disponible")}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="row">
        <div className="col-md-6 d-flex align-items-center">
          <span>
            {translate("Displaying")} {list_history.length}{" "}
            {translate("out of")} {total}
          </span>
        </div>
        <div className="col-md-6">
          <ReactPaginate
            previousLabel={translate("previous")}
            nextLabel={translate("next")}
            breakLabel={"..."}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            containerClassName={"pagination justify-content-end"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            activeClassName={"active"}
            forcePage={currentPage - 1}
          />
        </div>
      </div>

      {/* Modals */}

      <DownloadModal
        show={showDownloadModal}
        onHide={() => setShowDownloadModal(false)}
        onDownloadConfirm={onDownloadConfirm}
      />
      <UserHistoryDrawer
        show={showHistoryDrawer}
        history={selectedHistory}
        onHide={() => setShowHistoryDrawer(false)}
      />
    </>
  );
};

export default HistoryUser;
